import {
  forwardRef,
  useMemo,
  useRef,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';

import { ChevronUp, Mic } from 'lucide-react';

import { useConversationContext } from '@/contexts/v2/ConversationContext';
import { derivePeekStatus } from './peekStatus';

export type ConversationPeekBarProps = {
  onOpen: () => void;
  onOpenAndStartVoice?: () => void;
  /**
   * Status label shown before the body text (e.g. "Anaka replied",
   * "Ready when you are", "Thinking…", "Responding"). When omitted,
   * derived from context via derivePeekStatus (FR-007).
   */
  status?: string;
  /**
   * Body text after the status. When omitted, falls back to the snippet
   * derived from the last assistant turn's text (truncated to ~200 chars).
   */
  body?: string;
  /**
   * Accent colour for the status label. When omitted, derived from
   * derivePeekStatus (FR-007). Documented variants:
   *   - "Anaka replied"       → #7C3AED (purple)
   *   - "Ready when you are"  → #22C55E (green)
   *   - "Thinking…"           → #60A5FA (blue)
   *   - "Responding"          → #F59E0B (amber)
   */
  accent?: string;
};

export const ConversationPeekBar = forwardRef<HTMLDivElement, ConversationPeekBarProps>(
  function ConversationPeekBar(
    { onOpen, onOpenAndStartVoice, status, body, accent },
    ref,
  ) {
    const { turns, inputMode, isThinking, isResponding, voiceState } = useConversationContext();
    const lastAssistantTurn = useMemo(
      () => [...turns].reverse().find((t) => t.role === 'assistant'),
      [turns],
    );
    const lastText = useMemo(() => {
      if (!lastAssistantTurn) return '';
      return lastAssistantTurn.content
        .filter((b): b is { type: 'text'; text: string } => b.type === 'text')
        .map((b) => b.text)
        .join(' ')
        .slice(0, 200);
    }, [lastAssistantTurn]);

    // Derive default status/accent/snippet from runtime state (FR-007).
    // Explicit props take precedence over derived values.
    const derived = useMemo(
      () =>
        derivePeekStatus({
          isThinking,
          isResponding,
          voiceState,
          hasAnakaReply: !!lastAssistantTurn,
          lastReplyText: lastText || undefined,
        }),
      [isThinking, isResponding, voiceState, lastAssistantTurn, lastText],
    );

    // Status/body resolution: explicit prop wins → derivePeekStatus → placeholder.
    const resolvedStatus = status ?? derived.label;
    const resolvedBody = body !== undefined ? body : (derived.snippet ?? lastText);
    const resolvedAccent = accent ?? derived.accent;

    const startYRef = useRef<number | null>(null);
    const suppressNextClickRef = useRef(false);

    const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
      startYRef.current = e.clientY;
    };

    const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
      if (startYRef.current === null) return;
      const dy = startYRef.current - e.clientY;
      if (dy > 30) {
        startYRef.current = null;
        suppressNextClickRef.current = true;
        onOpen();
      }
    };

    const handlePointerUp = () => {
      startYRef.current = null;
    };

    const handlePointerCancel = () => {
      startYRef.current = null;
    };

    const handleClick = () => {
      if (suppressNextClickRef.current) {
        suppressNextClickRef.current = false;
        return;
      }
      onOpen();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleClick();
        return;
      }
      if (e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    };

    return (
      // Layout is controlled by the parent (ConversationShell wraps this in a
      // fixed-bottom flex column so the pill stacks above the input bar).
      // Keep `flex justify-center` so the inner pill is horizontally centred
      // within whatever width the parent gives us.
      <div
        ref={ref}
        data-testid="conversation-peek-bar"
        role="button"
        aria-label="Expand conversation"
        tabIndex={0}
        className="flex justify-center px-4 pb-1 pt-2"
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onKeyDown={handleKeyDown}
      >
        <div
          className="flex items-center gap-2.5 rounded-full border px-3 py-1.5 backdrop-blur-xl"
          style={{
            // Mobile width clamp per design: width: min(540px, 100%) — keeps
            // the pill from stretching across wide tablets.
            width: 'min(540px, 100%)',
            background: 'var(--chat-surface)',
            borderColor: 'var(--chat-border)',
            boxShadow: 'var(--chat-shadow)',
          }}
        >
          {/* Gradient "A" avatar — 18×18, matches PeekHint exactly */}
          <span
              aria-hidden
              className="grid shrink-0 place-items-center rounded-full font-semibold text-white"
              style={{
                width: 18,
                height: 18,
                fontSize: 8,
                background: 'linear-gradient(135deg, #7C3AED, #3B82F6)',
              }}
            >
              A
            </span>
          {/* Status label · snippet — single line, truncated (FR-007) */}
          <p
            className="flex-1 text-muted-foreground"
            style={{
              fontSize: 11.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            <span
              className="font-semibold"
              style={{ color: resolvedAccent }}
            >
              {resolvedStatus}
              {resolvedBody ? ' · ' : ''}
            </span>
            {resolvedBody ? <span>{resolvedBody}</span> : null}
          </p>
          {inputMode === 'voice' ? (
            <button
              type="button"
              aria-label="Start voice conversation"
              className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation();
                onOpenAndStartVoice?.();
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Mic className="size-4" aria-hidden />
            </button>
          ) : (
            /* ChevronUp — 12px per design handoff */
            <ChevronUp
              size={12}
              className="shrink-0 text-muted-foreground"
              aria-hidden
            />
          )}
        </div>
      </div>
    );
  },
);

ConversationPeekBar.displayName = 'ConversationPeekBar';
